import { type MessageKey } from '@ecom/i18n';

export type AccountKind = 'superadmin' | 'platform_admin' | 'shop';

export const KIND_LABEL_KEY: Record<AccountKind, MessageKey> = {
  superadmin: 'admin.access.kind.superadmin',
  platform_admin: 'admin.access.kind.platform_admin',
  shop: 'admin.access.kind.shop',
};

/** Mirrors `LockedAccount` of `apps/api/src/modules/identity/account-access.service.ts`. */
export interface LockedAccount {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly kind: AccountKind;
  readonly failedLoginCount: number;
  readonly lockedUntil: string;
}

export interface ProvisionalPassword {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly kind: AccountKind;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly expired: boolean;
}

export interface AccountSummary {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly kind: AccountKind;
  readonly status: string;
  readonly lockedUntil: string | null;
  readonly mustChangePassword: boolean;
  readonly lastLoginAt: string | null;
  readonly createdAt: string;
}

export interface AccountSession {
  readonly sessionId: string;
  readonly createdAt: string;
  readonly lastUsedAt: string;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
  readonly revokedReason: string | null;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly isActive: boolean;
}

/** How the identity of the person asking for a reset was checked, outside the platform (a closed list, like the API's). */
export const VERIFICATION_METHODS = ['phone_call', 'video_call', 'in_person'] as const;
export type VerificationMethod = (typeof VERIFICATION_METHODS)[number];

export const METHOD_LABEL_KEY: Record<VerificationMethod, MessageKey> = {
  phone_call: 'admin.access.reset.method.phone_call',
  video_call: 'admin.access.reset.method.video_call',
  in_person: 'admin.access.reset.method.in_person',
};

export const SESSION_REASON_LABEL_KEY: Record<string, MessageKey> = {
  logout: 'admin.access.sessions.reason.logout',
  logout_all: 'admin.access.sessions.reason.logout_all',
  token_reuse_detected: 'admin.access.sessions.reason.token_reuse_detected',
  password_changed: 'admin.access.sessions.reason.password_changed',
  expired: 'admin.access.sessions.reason.expired',
  role_changed: 'admin.access.sessions.reason.role_changed',
  account_disabled: 'admin.access.sessions.reason.account_disabled',
  admin_revoked: 'admin.access.sessions.reason.admin_revoked',
};
